import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Percent } from 'lucide-react';
import type { FiscalParameter, FiscalParameterForm, ModalState } from '../types/system-parameters.types';

interface FiscalParameterModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: FiscalParameterForm, isEdit?: boolean, itemId?: string) => void;
}

export const FiscalParameterModal: React.FC<FiscalParameterModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FiscalParameterForm>({
    code: '',
    name: '',
    type: 'percentage',
    value: 0,
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const fiscal = modalState.item as FiscalParameter;
        setFormData({
          code: fiscal.code,
          name: fiscal.name,
          type: fiscal.type,
          value: Number(fiscal.value),
          description: fiscal.description || ''
        });
      } else {
        // Resetear para modo crear
        setFormData({
          code: '',
          name: '',
          type: 'percentage',
          value: 0,
          description: ''
        });
      }
      setErrors({});
    }
  }, [modalState]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!formData.code.trim()) {
      newErrors.code = 'El código es obligatorio';
    } else if (!/^[A-Z_]+$/.test(formData.code.trim())) {
      newErrors.code = 'El código debe contener solo letras mayúsculas y guiones bajos';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.type) {
      newErrors.type = 'El tipo es obligatorio';
    }

    if (formData.value <= 0) {
      newErrors.value = 'El valor debe ser mayor a 0';
    } else if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'El porcentaje no puede ser mayor a 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const isEdit = modalState.mode === 'edit';
      const itemId = modalState.item?.id;
      
      onSave(formData, isEdit, itemId);
      onClose();
    } catch (error) {
      console.error('Error al guardar parámetro fiscal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FiscalParameterForm, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getValueLabel = () => {
    return formData.type === 'percentage' ? 'Porcentaje (%)' : 'Valor (S/)';
  };

  const getValuePlaceholder = () => {
    return formData.type === 'percentage' ? '18.00' : '100.00';
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-macartur-primary" />
            {modalState.mode === 'edit' ? 'Editar Parámetro Fiscal' : 'Agregar Parámetro Fiscal'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Código */}
          <div className="space-y-2">
            <Label htmlFor="code">
              Código <span className="text-red-500">*</span>
            </Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
              placeholder="IGV_PERCENT"
              className={errors.code ? 'border-red-500' : ''}
              disabled={modalState.mode === 'edit'} // No se puede editar el código
            />
            {errors.code && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.code}
              </p>
            )}
          </div>

          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="IGV - Impuesto General a las Ventas"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Tipo */}
          <div className="space-y-2">
            <Label htmlFor="type">
              Tipo <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.type} 
              onValueChange={(value) => handleInputChange('type', value as 'percentage' | 'currency')}
              disabled={modalState.mode === 'edit'} // No se puede cambiar el tipo
            >
              <SelectTrigger className={errors.type ? 'border-red-500' : ''}>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                <SelectItem value="currency">Moneda (S/)</SelectItem>
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.type}
              </p>
            )}
          </div>

          {/* Valor */}
          <div className="space-y-2">
            <Label htmlFor="value">
              {getValueLabel()} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="value"
              type="number"
              step="0.01"
              min="0"
              max={formData.type === 'percentage' ? '100' : undefined}
              value={formData.value}
              onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
              placeholder={getValuePlaceholder()}
              className={errors.value ? 'border-red-500' : ''}
            />
            {errors.value && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {errors.value}
              </p>
            )}
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descripción del parámetro fiscal..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-macartur-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (modalState.mode === 'edit' ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};